import { LocalizedEventData, ValidTimeSlot } from '@carlito/bookito-client';
import { useCallback, useEffect, useState } from 'react';
import Breadcrumb from './Breadcrumbs';
import DateSelectionForm from './DateSelection';
import ParticularsSelectionForm, { AdditionalFormRenderingProps } from './ParticularsInfoSelection';
import TimeSlotSelectionForm from './TimeslotsSelection';
import './style.css';
export type Step = { name: string; id: string };

export type BookingParams = {
  numParticipants?: number;
  selectedDateTime?: Date;
  priceVariants?: string[];
  selectedTimeSlot?: ValidTimeSlot;
  comment?: string;
};

export type EventInfo = {
  id: string;
  occurrences: LocalizedEventData['occurrences'];
  bookability: string;
  timeslotDuration: number;
};

export type BookitoStepperBookingRequestFormParams = {
  eventPriceVariants?: {
    id: string;
    displayName: string;
    price: number | null;
  }[];
  isTimeSlotEnabledEvent: boolean;
  bookingParams: BookingParams;
  updateBookingParams: (params: BookingParams) => void;
  maxParticipants: number | null | undefined;
  availableOnDates: string[];
  onSubmitBooking: () => void;
  eventInfo: EventInfo;
  updateSuccessMsg: (msg: string) => void;
  userLanguage: string;
  numParticipants?: number;
  ownerAuthId?: string;
  updateGuestFormDisplayState: (state: boolean) => void;
  showSubmitButton?: boolean;
  showExtraFields?: boolean;
} & AdditionalFormRenderingProps;

const stepsObject: Step[] = [
  { name: 'bookito-stepper-form-steps-date', id: 'calendar' },
  { name: 'bookito-stepper-form-steps-time', id: 'time' },
  { name: 'bookito-stepper-form-steps-guests', id: 'guest' },
  { name: 'bookito-stepper-form-steps-complete', id: 'final-state' },
];

const stepIds = stepsObject.map((step) => step.id);

export const BookitoStepperBookingRequestForm = (
  params: BookitoStepperBookingRequestFormParams
) => {
  const {
    eventPriceVariants,
    isTimeSlotEnabledEvent,
    bookingParams,
    updateBookingParams,
    maxParticipants,
    availableOnDates,
    onSubmitBooking,
    eventInfo,
    updateSuccessMsg,
    ownerAuthId,
    userLanguage,
    updateGuestFormDisplayState,
    showSubmitButton = true,
    showExtraFields = true,
    ...formRenderingProps
  } = params;
  const [steps] = useState<Step[]>(stepsObject);
  const [stepState, updateStepState] = useState<string | undefined>(undefined);

  const [guestState] = stepsObject.filter((el) => el.id === 'guest');

  useEffect(() => {
    if (stepState === guestState.id) {
      updateGuestFormDisplayState(true);
    } else {
      updateGuestFormDisplayState(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepState]);

  const numParticipants = bookingParams?.numParticipants ?? 1;

  const onParticipantsChange = useCallback(
    (numParticipantsParam: number) => {
      const numParticipants = Math.max(1, numParticipantsParam);
      updateBookingParams({
        ...bookingParams,
        numParticipants,
      });
    },
    [bookingParams, updateBookingParams]
  );

  const onCommentUpdate = useCallback(
    (comment: string) => {
      updateBookingParams({
        ...bookingParams,
        comment,
      });
    },
    [bookingParams, updateBookingParams]
  );

  const getNextStep = () => {
    let nextStep = stepIds[2];

    if (stepState === stepIds[0]) {
      nextStep = stepIds[1];
    }

    if (stepState === stepIds[1]) {
      nextStep = stepIds[2];
    }

    return nextStep;
  };

  // @TODO: this logic was implemented to pass through time slot state
  // Since this is as of now not implemented, we are commenting it.
  // useEffect(() => {
  //   if (
  //     getNextStep() === stepIds[2] &&
  //     bookingParams.selectedDateTime &&
  //     !bookingParams.selectedTimeSlot
  //   ) {
  //     const dateComponent = bookingParams.selectedDateTime?.toISOString().split('T')[0];
  //     const timeComponent = eventInfo.occurrences[0].startDateTime.split('T')[1];

  //     updateBookingParams({
  //       ...bookingParams,
  //       selectedTimeSlot: {
  //         startDateTime: new Date(`${dateComponent}T${timeComponent}`).toISOString(),
  //       },
  //     });
  //   }
  // }, [bookingParams.selectedDateTime]);

  useEffect(() => {
    let defaultStep = stepIds[0];
    if (bookingParams.selectedDateTime) defaultStep = stepIds[1];
    updateStepState(defaultStep);
  }, []);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {stepState && (
        <>
          <Breadcrumb
            steps={steps}
            updateStepState={updateStepState}
            bookingParams={bookingParams}
            stepState={stepState}
            userLanguage={userLanguage}
            updateBookingParams={updateBookingParams}
          />

          {stepState === stepIds[0] && (
            <DateSelectionForm
              updateStepState={updateStepState}
              updateBookingParams={updateBookingParams}
              nextStep={getNextStep()}
              availableOnDates={availableOnDates}
              bookingParams={bookingParams}
            />
          )}

          {stepState === stepIds[1] && (
            <TimeSlotSelectionForm
              bookingParams={bookingParams}
              updateStepState={updateStepState}
              updateBookingParams={updateBookingParams}
              nextStep={getNextStep()}
              eventInfo={eventInfo}
              maxParticipants={maxParticipants}
              ownerAuthId={ownerAuthId}
            />
          )}

          {stepState === stepIds[2] && (
            <ParticularsSelectionForm
              showExtraFields={showExtraFields}
              showSubmitButton={showSubmitButton}
              bookingParams={bookingParams}
              onBookingParamsChange={updateBookingParams}
              eventPriceVariants={eventPriceVariants}
              numParticipants={numParticipants}
              maxParticipants={maxParticipants}
              onParticipantsChange={onParticipantsChange}
              onSubmitBooking={onSubmitBooking}
              eventInfo={eventInfo}
              isTimeSlotEnabledEvent={isTimeSlotEnabledEvent}
              updateSuccessMsg={updateSuccessMsg}
              onCommentUpdate={onCommentUpdate}
              ownerAuthId={ownerAuthId}
              {...formRenderingProps}
            />
          )}
        </>
      )}
    </>
  );
};
